import { InlineLoadingSkeleton } from "@2u-landing-pages/components/LoadingSpinner";
import { Tooltip } from "@2u-landing-pages/components/Tooltip";
import { useStringInterpolation } from "@2u-landing-pages/utils";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Block, BLOCKS, Inline, MARKS } from "@contentful/rich-text-types";
import React from "react";
import { OneTrustLink } from "../templates/Footer/OneTrustLink";
import EventClient from "./event-client";

export const renderMark = {
  [MARKS.BOLD]: (text: React.ReactNode | string) => <strong>{text}</strong>,
  [MARKS.ITALIC]: (text: React.ReactNode | string) => <em>{text}</em>,
  [MARKS.STRIKETHROUGH]: (text: React.ReactNode | string) => <s>{text}</s>,
};

export const hyperLinkRenderer = (node: Block | Inline, location = ""): React.ReactNode => {
  const { uri } = node?.data as { uri?: string };
  const content = node?.content?.[0];

  if (!content || !("value" in content)) {
    return null;
  }

  const handleExternalLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    EventClient.elementClicked({
      webElement: {
        htmlId: event.currentTarget.href,
        location,
        name: event.currentTarget.innerText,
        position: "",
        text: "",
      },
    });
  };

  return (
    <a href={uri} target="_blank" rel="noopener noreferrer" onClick={handleExternalLinkClick}>
      {content.value}
    </a>
  );
};

const PHONE_NUMBER_REGEX = /{{phone_number}}/g;

export const renderText = (text: string, phoneNumber = ""): React.ReactNode[] => {
  let processedText = text;
  if (phoneNumber) {
    processedText = text.replace(PHONE_NUMBER_REGEX, phoneNumber);
  }

  const parts = processedText.includes("<s>") ? parseStrikethroughText(processedText) : [processedText];

  const result: React.ReactNode[] = [];
  parts.forEach((part, index) => {
    if (typeof part === "string" && part.includes("\n")) {
      const segments = part.split("\n");
      segments.forEach((seg, i) => {
        result.push(seg);
        if (i < segments.length - 1) result.push(<br key={`br_${index}_${i}`} />);
      });
    } else {
      result.push(part);
    }
  });

  return result;
};

export const renderFooterText = (text: string, phone_number: string = "", isExeced: boolean) => {
  const placeholder_regex = /({{phone_number}}|{{one_trust_privacy_link}})/g;

  return text.split(placeholder_regex).map((segment, index) => {
    switch (segment) {
      case "{{phone_number}}":
        return (
          <a key={index} href={`tel:${phone_number}`}>
            {phone_number}
          </a>
        );
      case "{{one_trust_privacy_link}}":
        return <OneTrustLink>Your Privacy {isExeced ? "Choices" : "Rights"}</OneTrustLink>;
      default:
        return renderText(segment);
    }
  });
};

const InlineContentRenderer = ({
  content,
  phoneNumber
}: {
  content: any;
  phoneNumber?: string;
}) => {
  const { parseText, isLoading } = useStringInterpolation(content);

  if (isLoading) {
    return <InlineLoadingSkeleton />;
  }

  return (
    <>
      {documentToReactComponents(content, {
        renderMark,
        renderText: (text: string) => renderText(parseText(text) ?? text, phoneNumber),
        renderNode: {
          [BLOCKS.PARAGRAPH]: (node, children) => <span>{children}</span>,
        },
      })}
    </>
  );
};

export const renderEmbeddedEntry = (
  node?: Block | Inline,
  location?: string,
  parentContent?: Inline | Block,
  phoneNumber?: string,
  parseMetadata?: (text: string) => string,
  iconSize?: "sm" | "md"
) => {
  if (node) {
    const tooltip = node?.data?.target?.sys?.contentType?.sys?.id === "tooltip";
    const isInlineContent = node?.data?.target?.sys?.contentType?.sys?.id === "inline_content";

    if (tooltip || (isInlineContent && node.data.target.fields.tooltipPosition)) {
      // body, content, icon_size, position come from tooltip and will be removed in the future.
      const { body, content, icon_size, position, tooltipPosition } = node.data.target.fields;
      return (
        <Tooltip
          body={content || body}
          icon_size={icon_size || iconSize || "sm"}
          position={tooltipPosition || position}
          location={location}
          parentContent={parentContent}
          phoneNumber={phoneNumber}
        />
      );
    } else if (isInlineContent) {
      return (
        <InlineContentRenderer
          content={node?.data.target.fields.content}
          phoneNumber={phoneNumber}
        />
      );
    }

    return null;
  }

  return null;
};

export const findPrivacyPolicyLink = (content: any): string | null => {
  if (!content || !Array.isArray(content)) return null;

  for (const node of content) {
    if (node?.nodeType === "hyperlink") {
      const hasPrivacyPolicyText = node?.content?.some(
        (textNode: any) =>
          textNode?.nodeType === "text" && textNode?.value?.trim()?.toLowerCase()?.includes("privacy policy")
      );

      if (hasPrivacyPolicyText) {
        return node?.data?.uri || null;
      }
    }

    // Recursively search in nested content
    if (node?.content) {
      const found = findPrivacyPolicyLink(node.content);
      if (found) return found;
    }
  }

  return null;
};

export const parseStrikethroughText = (str: string): React.ReactNode[] => {
  const parts = str.split(/(<s>.*?<\/s>)/);
  return parts.map((part, i) => {
    if (part.startsWith("<s>") && part.endsWith("</s>")) {
      return (
        <span key={i} style={{ textDecoration: "line-through" }}>
          {part.slice(3, -4)}
        </span>
      );
    }
    return part;
  });
};
